// Storage validators derived from the shared contracts (CC-16). `packages/
// shared` may not import `convex`, so each union is built here from the
// shared tuple and pinned to the shared type: a contract change that the
// stored shape cannot follow is a compile error, not an unreadable row.
import type { Bookmark } from "@bindery/shared/reader/bookmarks";
import {
  CLIENT_FLUENCY_RESPONSE_SOURCES,
  FLUENCY_RESPONSE_SOURCES,
  type ClientFluencyResponseSource,
  type FluencyResponseSource,
} from "@bindery/shared/contracts/fluency";
import {
  CONTROL_SOURCES,
  INTERACTION_COMPLETIONS,
  Treatment,
  TreatmentId,
  type ControlSource,
  type InteractionCompletion,
  type LearningRecordContext,
} from "@bindery/shared/contracts/learning";
import {
  ASSESSMENT_ITEM_RESULTS,
  ASSESSMENT_KINDS,
  AUDIO_SUPPLY_CAUSES,
  AWRF_TIMED_SEGMENT_OUTCOMES,
  ORIENTATION_RUN_STATUSES,
  READER_CONTENT_STATUSES,
  RECORDING_STATUSES,
  WORDS_READ_PHASES,
  WRF_PERCENTILE_STATUSES,
  type AssessmentKind,
  type AudioSupplyCause,
  type AwrfTimedSegmentOutcome,
  type OrientationRunStatus,
  type ReaderContentStatus,
  type RecordingStatus,
  type WordsReadPhase,
  type WrfPercentileStatus,
} from "@bindery/shared/contracts/orientation";
import type { AssessmentMarkResult } from "@bindery/shared/contracts/assessment-sync";
import type { AwrfStratum } from "@bindery/shared/reader/awrf-policy";
import {
  ORF_SKIPPED_REASONS,
  type OrfSkippedReason,
} from "@bindery/shared/reader/orf-skip";
import type { Assert, SameKeys, SameSet } from "@bindery/shared/contracts/pin";
import {
  ASSIGNMENT_ARMS,
  ASSIGNMENT_COMMIT_SOURCES,
  EarlyExitReason,
  EnrollmentStatus,
  ExclusionReason,
  ORIENTATION_PHASE_ORDER,
  OrientationResponseType,
  PARENT_ENROLLMENT_CHOICES,
  STORED_RECORDING_CONSENTS,
  type AssignmentArm,
  type AssignmentCommitSource,
  type OrientationPhase,
  type ParentEnrollmentChoice,
  type StoredRecordingConsent,
} from "@bindery/shared/contracts/study";
import { v, type Infer, type VLiteral, type VUnion } from "convex/values";

export function literals<T extends string>(
  values: readonly T[],
): VUnion<T, VLiteral<T>[]> {
  if (values.length === 0) throw new Error("literals() needs a value");
  return v.union(...values.map((value) => v.literal(value)));
}

export const controlSource = literals(CONTROL_SOURCES);
export type ControlSourcePin = Assert<
  SameSet<Infer<typeof controlSource>, ControlSource>
>;

export const completion = literals(INTERACTION_COMPLETIONS);
export type CompletionPin = Assert<
  SameSet<Infer<typeof completion>, InteractionCompletion>
>;

type StoredTreatment = Exclude<Treatment, typeof Treatment.None>;
export const treatment = literals(
  Object.values(Treatment).filter(
    (value): value is StoredTreatment => value !== Treatment.None,
  ),
);
export type TreatmentPin = Assert<
  SameSet<Infer<typeof treatment>, StoredTreatment>
>;

export const treatmentId = literals(Object.values(TreatmentId));
export type TreatmentIdPin = Assert<
  SameSet<Infer<typeof treatmentId>, TreatmentId>
>;

export const fluencyResponseSource = literals(FLUENCY_RESPONSE_SOURCES);
export type FluencyResponseSourcePin = Assert<
  SameSet<Infer<typeof fluencyResponseSource>, FluencyResponseSource>
>;

// learning.submit's source: "awrf" rows are scored from aide marks and only the
// server writes them, so a reader's outbox must not be able to send one.
export const clientFluencyResponseSource = literals(
  CLIENT_FLUENCY_RESPONSE_SOURCES,
);
export type ClientFluencyResponseSourcePin = Assert<
  SameSet<
    Infer<typeof clientFluencyResponseSource>,
    ClientFluencyResponseSource
  >
>;

export const enrollmentStatus = literals(Object.values(EnrollmentStatus));
export type EnrollmentStatusPin = Assert<
  SameSet<Infer<typeof enrollmentStatus>, EnrollmentStatus>
>;

export const exclusionReason = literals(Object.values(ExclusionReason));
export type ExclusionReasonPin = Assert<
  SameSet<Infer<typeof exclusionReason>, ExclusionReason>
>;

export const parentEnrollmentChoice = literals(PARENT_ENROLLMENT_CHOICES);
export type ParentEnrollmentChoicePin = Assert<
  SameSet<Infer<typeof parentEnrollmentChoice>, ParentEnrollmentChoice>
>;

export const wrfPercentileStatus = literals(WRF_PERCENTILE_STATUSES);
export type WrfPercentileStatusPin = Assert<
  SameSet<Infer<typeof wrfPercentileStatus>, WrfPercentileStatus>
>;

export const orientationPhase = literals(ORIENTATION_PHASE_ORDER);
export type OrientationPhasePin = Assert<
  SameSet<Infer<typeof orientationPhase>, OrientationPhase>
>;

export const earlyExitReason = literals(Object.values(EarlyExitReason));
export type EarlyExitReasonPin = Assert<
  SameSet<Infer<typeof earlyExitReason>, EarlyExitReason>
>;

export const assessmentItemResult = literals(ASSESSMENT_ITEM_RESULTS);
export type AssessmentItemResultPin = Assert<
  SameSet<Infer<typeof assessmentItemResult>, AssessmentMarkResult>
>;

export const awrfTimedSegmentOutcome = literals(AWRF_TIMED_SEGMENT_OUTCOMES);
export type AwrfTimedSegmentOutcomePin = Assert<
  SameSet<Infer<typeof awrfTimedSegmentOutcome>, AwrfTimedSegmentOutcome>
>;

export const readerContentStatus = literals(READER_CONTENT_STATUSES);
export type ReaderContentStatusPin = Assert<
  SameSet<Infer<typeof readerContentStatus>, ReaderContentStatus>
>;

export const wordsReadPhase = literals(WORDS_READ_PHASES);
export type WordsReadPhasePin = Assert<
  SameSet<Infer<typeof wordsReadPhase>, WordsReadPhase>
>;

export const assessmentKind = literals(ASSESSMENT_KINDS);
export type AssessmentKindPin = Assert<
  SameSet<Infer<typeof assessmentKind>, AssessmentKind>
>;

export const runStatus = literals(ORIENTATION_RUN_STATUSES);
export type RunStatusPin = Assert<
  SameSet<Infer<typeof runStatus>, OrientationRunStatus>
>;

export const audioSupplyCause = literals(AUDIO_SUPPLY_CAUSES);
export type AudioSupplyCausePin = Assert<
  SameSet<Infer<typeof audioSupplyCause>, AudioSupplyCause>
>;

export const orfSkippedReason = literals(ORF_SKIPPED_REASONS);
export type OrfSkippedReasonPin = Assert<
  SameSet<Infer<typeof orfSkippedReason>, OrfSkippedReason>
>;

export const awrfStratum = literals([
  "predicted_known",
  "predicted_unknown",
  "shortfall_fill",
] as const);
export type AwrfStratumPin = Assert<
  SameSet<Infer<typeof awrfStratum>, AwrfStratum>
>;

export const recordingStatus = literals(RECORDING_STATUSES);
export type RecordingStatusPin = Assert<
  SameSet<Infer<typeof recordingStatus>, RecordingStatus>
>;

// "pending" is never stored: an absent field is pending.
export const recordingConsent = literals(STORED_RECORDING_CONSENTS);
export type RecordingConsentPin = Assert<
  SameSet<Infer<typeof recordingConsent>, StoredRecordingConsent>
>;

export const assignmentArm = literals(ASSIGNMENT_ARMS);
export type AssignmentArmPin = Assert<
  SameSet<Infer<typeof assignmentArm>, AssignmentArm>
>;

export const assignmentCommitSource = literals(ASSIGNMENT_COMMIT_SOURCES);
export type AssignmentCommitSourcePin = Assert<
  SameSet<Infer<typeof assignmentCommitSource>, AssignmentCommitSource>
>;

export const orientationResponseType = literals(
  Object.values(OrientationResponseType),
);
export type OrientationResponseTypePin = Assert<
  SameSet<Infer<typeof orientationResponseType>, OrientationResponseType>
>;

// Q1: camelCase, integers as int64, and the fields the row already carries
// at top level (response, actionId, treatmentId, readerId, sessionId) stored
// once. Changes are additive only: a removed field would reject batches
// already queued in browsers.
export const learningRecordContext = v.object({
  word: v.optional(v.string()),
  prediction: v.optional(
    v.object({
      responseId: v.int64(),
      confidence: v.optional(v.number()),
    }),
  ),
  sessionStartTime: v.optional(v.number()),
  createdAt: v.number(),
  pageStartTime: v.optional(v.number()),
  readingRate: v.optional(v.int64()),
  targetFluencyRecord: v.optional(v.array(v.int64())),
  precedingFluencyRecord: v.optional(v.array(v.int64())),
  choicesPresented: v.optional(v.array(v.string())),
  incorrectMatches: v.optional(v.array(v.string())),
  incorrectMatchesTruncated: v.optional(v.literal(true)),
  utcOffset: v.optional(v.number()),
  wordInReading: v.optional(
    v.object({
      index: v.int64(),
      readingId: v.string(),
    }),
  ),
  controlSource: v.optional(controlSource),
  completion: v.optional(completion),
  completedAt: v.optional(v.number()),
  lookBack: v.optional(v.boolean()),
  repeatTap: v.optional(v.boolean()),
  playbackFailed: v.optional(v.boolean()),
  historyCold: v.optional(v.literal(true)),
  bridgeSetVersion: v.optional(v.string()),
  servedAt: v.optional(v.number()),
});

// `position` is yominoki's, and `recovered` served only ../app's pre-v3
// backlog, which has no rows to carry.
type StoredContextKeys = Omit<
  LearningRecordContext,
  | "response"
  | "actionId"
  | "treatmentId"
  | "readerId"
  | "sessionId"
  | "position"
  | "recovered"
>;
// Key set only: the stored int64 fields are bigint where the contract has
// number, so Exact could never hold.
export type LearningRecordContextPin = Assert<
  SameKeys<Infer<typeof learningRecordContext>, StoredContextKeys>
>;

export const bookmark = v.object({
  readingId: v.string(),
  currentPage: v.int64(),
  lastupdate: v.number(),
  completed: v.optional(v.boolean()),
  stars: v.optional(v.int64()),
});
// Key set only, for the same reason: currentPage and stars are int64 here.
export type BookmarkPin = Assert<SameKeys<Infer<typeof bookmark>, Bookmark>>;
